import { useState, useEffect, useCallback } from 'react';
import type { IUseMessengerRetriggerProps, IUseMessengerRetriggerReturn } from '@/@types/leads';

const STUCK_THRESHOLD_MS = 120_000; // 2 minutes

/**
 * Detects when a messenger check has been running for longer than STUCK_THRESHOLD_MS.
 * Returns showRetrigger=true after the threshold, and resetRetrigger to clear it.
 * Used by MessengerIcons (in table rows) and LeadDetailInfo (in detail panel).
 */
export function useMessengerRetrigger({
  isDetecting,
  checkStartedAt,
}: IUseMessengerRetriggerProps): IUseMessengerRetriggerReturn {
  const [_showRetrigger, setShowRetrigger] = useState(false);

  useEffect(() => {
    if (!isDetecting || !checkStartedAt) {
      return;
    }
    const elapsed = Date.now() - checkStartedAt;
    const delay = elapsed >= STUCK_THRESHOLD_MS ? 0 : STUCK_THRESHOLD_MS - elapsed;
    const timer = setTimeout(() => setShowRetrigger(true), delay);
    return () => clearTimeout(timer);
  }, [isDetecting, checkStartedAt]);

  const resetRetrigger = useCallback(() => setShowRetrigger(false), []);

  // Derive: only show retrigger if we're still detecting AND the timer has fired
  const showRetrigger = _showRetrigger && isDetecting;

  return { showRetrigger, resetRetrigger };
}
